// src/components/TrainerSchedule.js (Optimized Version)
import { API_URL } from '../config/apiConfig';

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import {
    Box,
    Typography,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    CircularProgress,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Chip,
    Grid,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import BarChartIcon from '@mui/icons-material/BarChart';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import BeachAccessIcon from '@mui/icons-material/BeachAccess';
import TrainerStatsModal from './TrainerStatsModal';
import TrainerColorLegend from './TrainerColorLegend';
import TrainerWeeklyNotes from './TrainerWeeklyNotes';
import TrainerScheduleMobile from './TrainerScheduleMobile';
import TrainerVacationModal from './TrainerVacationModal';

const CalendarPaper = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    boxShadow: theme.shadows[2],
    overflow: 'hidden',
}));

// Color palette for trainers - memoized to prevent recreation
const trainerColors = [
    { bg: '#1976d2', text: '#ffffff' }, // Blue
    { bg: '#388e3c', text: '#ffffff' }, // Green
    { bg: '#f57c00', text: '#ffffff' }, // Orange
    { bg: '#7b1fa2', text: '#ffffff' }, // Purple
    { bg: '#d32f2f', text: '#ffffff' }, // Red
    { bg: '#00796b', text: '#ffffff' }, // Teal
    { bg: '#5d4037', text: '#ffffff' }, // Brown
    { bg: '#303f9f', text: '#ffffff' }, // Indigo
    { bg: '#c2185b', text: '#ffffff' }, // Pink
    { bg: '#455a64', text: '#ffffff' }, // Blue Grey
    { bg: '#6a1b9a', text: '#ffffff' }, // Dark Purple
    { bg: '#2e7d32', text: '#ffffff' }, // Dark Green
    { bg: '#e65100', text: '#ffffff' }, // Deep Orange
    { bg: '#ad1457', text: '#ffffff' }, // Deep Pink
    { bg: '#1565c0', text: '#ffffff' }, // Dark Blue
];


export default function TrainerSchedule({ affiliate, trainers }) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const calendarRef = useRef(null);
    
    const [classes, setClasses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [isEditingPayable, setIsEditingPayable] = useState(false);
    const [selectedTrainer, setSelectedTrainer] = useState('');
    const [pendingChanges, setPendingChanges] = useState(new Map());
    const [pendingPayableChanges, setPendingPayableChanges] = useState(new Map());
    const [successDialogOpen, setSuccessDialogOpen] = useState(false);
    const [statsModalOpen, setStatsModalOpen] = useState(false);
    const [vacationModalOpen, setVacationModalOpen] = useState(false);
    const [legendExpanded, setLegendExpanded] = useState(false);
    const [filterTrainer, setFilterTrainer] = useState('');
    const [isFiltering, setIsFiltering] = useState(false);
    const [currentWeekStart, setCurrentWeekStart] = useState(null);
    const [visibleWeeks, setVisibleWeeks] = useState([]);
    const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

    // Memoized user role check
    const isAffiliate = useMemo(() => {
        return localStorage.getItem('role') === 'affiliate';
    }, []);

    // Calculate the Monday of the current week being viewed
    const getMonday = useCallback((date) => {
        const d = new Date(date);
        const day = d.getDay();
        const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
        const monday = new Date(d.setDate(diff));
        monday.setHours(0, 0, 0, 0);
        return monday;
    }, []);

    // Handle calendar dates change (when user navigates between months/weeks)
    const handleDatesSet = useCallback((dateInfo) => {
        const monday = getMonday(dateInfo.start);
        setCurrentWeekStart(monday);
        
        // Update current year based on calendar view
        // Use the view's current date (middle of the visible range) to get accurate year
        if (dateInfo.view && dateInfo.view.currentStart) {
            const viewYear = new Date(dateInfo.view.currentStart).getFullYear();
            setCurrentYear(viewYear);
        } else {
            // Fallback: use middle date of the visible range
            const startTime = new Date(dateInfo.start).getTime();
            const endTime = new Date(dateInfo.end).getTime();
            const middleTime = startTime + (endTime - startTime) / 2;
            const viewYear = new Date(middleTime).getFullYear();
            setCurrentYear(viewYear);
        }
        
        // Calculate all visible weeks in the calendar view
        // FullCalendar shows weeks that may include days from previous/next month
        // We need to get the actual week rows from the calendar DOM
        const weeks = [];
        const startDate = new Date(dateInfo.start);
        const endDate = new Date(dateInfo.end);
        
        // Start from the Monday of the first visible date
        let currentMonday = getMonday(startDate);
        
        // Continue until we've covered all visible dates
        while (currentMonday < endDate) {
            weeks.push(new Date(currentMonday));
            currentMonday.setDate(currentMonday.getDate() + 7);
        }
        
        // Ensure we have at least one week
        if (weeks.length === 0) {
            weeks.push(getMonday(startDate));
        }
        
        setVisibleWeeks(weeks);
    }, [getMonday]);

    // Set initial week on mount
    useEffect(() => {
        if (!currentWeekStart) {
            const today = new Date();
            const monday = getMonday(today);
            setCurrentWeekStart(monday);
            
            // Set initial visible weeks (current month)
            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
            
            const weeks = [];
            let currentMonday = getMonday(startOfMonth);
            const endDate = new Date(endOfMonth);
            endDate.setDate(endDate.getDate() + 7); // Add buffer
            
            while (currentMonday < endDate) {
                weeks.push(new Date(currentMonday));
                currentMonday.setDate(currentMonday.getDate() + 7);
            }
            
            setVisibleWeeks(weeks);
        }
    }, [currentWeekStart, getMonday]);

    // Memoized trainer color function
    const getTrainerColor = useCallback((trainerName, trainers) => {
        if (!trainerName || trainerName === 'Unassigned') {
            return { bg: '#f5f5f5', text: '#666666' };
        }

        const trainerIndex = trainers.findIndex(trainer => trainer.fullName === trainerName);

        if (trainerIndex === -1) {
            return { bg: '#f5f5f5', text: '#666666' };
        }

        const colorIndex = trainerIndex % trainerColors.length;
        return trainerColors[colorIndex];
    }, []);

    // Optimized load function with debouncing
    const loadClassSchedules = useCallback(async () => {
        if (!affiliate?.id) return;

        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/trainer-schedule/${affiliate.id}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                throw new Error('Failed to load class schedules');
            }

            const data = await response.json();
            setClasses(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [affiliate?.id]);

    // Load on mount - only when affiliate changes
    useEffect(() => {
        loadClassSchedules();
    }, [loadClassSchedules]);

    // Memoized filtered classes - used by both mobile and desktop views
    const filteredClasses = useMemo(() => {
        let filtered = classes;

        // Apply trainer filter if active
        if (isFiltering && filterTrainer) {
            const selectedTrainerName = trainers.find(t => t.trainerId.toString() === filterTrainer)?.fullName;
            filtered = classes.filter(classItem => {
                const pendingTrainer = pendingChanges.get(classItem.id);
                const trainerName = pendingTrainer || classItem.trainer || '';
                return trainerName === selectedTrainerName;
            });
        }

        return filtered;
    }, [classes, isFiltering, filterTrainer, trainers, pendingChanges]);

    // Memoized calendar events to prevent unnecessary recalculations
    const calendarEvents = useMemo(() => {
        return filteredClasses.map((classItem) => {
            const startTime = new Date(classItem.time);
            const endTime = new Date(startTime.getTime() + classItem.duration * 60000);

            const pendingTrainer = pendingChanges.get(classItem.id);
            const trainerName = pendingTrainer || classItem.trainer || '';

            const pendingPayable = pendingPayableChanges.get(classItem.id);
            const isPayable = pendingPayable !== undefined ? pendingPayable : classItem.payable;

            const trainerColor = getTrainerColor(trainerName, trainers);

            const backgroundColor = isPayable ? trainerColor.bg : '#f5f5f5';
            const textColor = isPayable ? trainerColor.text : '#666666';
            const borderColor = isPayable ? trainerColor.bg : '#e0e0e0';

            return {
                id: classItem.id,
                title: classItem.trainingName,
                start: startTime,
                end: endTime,
                backgroundColor: backgroundColor,
                borderColor: borderColor,
                textColor: textColor,
                extendedProps: {
                    classData: classItem,
                    trainerName: trainerName,
                    location: classItem.location,
                    memberCapacity: classItem.memberCapacity,
                    payable: isPayable
                }
            };
        });
    }, [filteredClasses, trainers, pendingChanges, pendingPayableChanges, getTrainerColor]);

    // Optimized event click handler
    const handleEventClick = useCallback((clickInfo) => {
        const classId = parseInt(clickInfo.event.id);

        if (isEditing && selectedTrainer) {
            const trainerToAssign = trainers.find(t => t.trainerId === selectedTrainer);

            if (trainerToAssign) {
                setPendingChanges(prev => new Map(prev.set(classId, trainerToAssign.fullName)));

                const trainerColor = getTrainerColor(trainerToAssign.fullName, trainers);

                // Only update FullCalendar event props if this is a FullCalendar event (desktop)
                if (clickInfo.event.setProp) {
                    clickInfo.event.setProp('backgroundColor', trainerColor.bg);
                    clickInfo.event.setProp('borderColor', trainerColor.bg);
                    clickInfo.event.setProp('textColor', trainerColor.text);
                    clickInfo.event.setExtendedProp('trainerName', trainerToAssign.fullName);
                }
            }
        } else if (isEditingPayable) {
            // Get current payable status - check both FullCalendar and direct class data
            const currentPayable = clickInfo.event.extendedProps?.payable ?? 
                                   classes.find(c => c.id === classId)?.payable ?? false;
            const newPayable = !currentPayable;

            setPendingPayableChanges(prev => new Map(prev.set(classId, newPayable)));

            // Only update FullCalendar event props if this is a FullCalendar event (desktop)
            if (clickInfo.event.setProp) {
                const trainerName = clickInfo.event.extendedProps?.trainerName || 
                                   classes.find(c => c.id === classId)?.trainer || '';
                const trainerColor = getTrainerColor(trainerName, trainers);
                const backgroundColor = newPayable ? trainerColor.bg : '#f5f5f5';
                const borderColor = newPayable ? trainerColor.bg : '#e0e0e0';
                const textColor = newPayable ? trainerColor.text : '#666666';

                clickInfo.event.setProp('backgroundColor', backgroundColor);
                clickInfo.event.setProp('borderColor', borderColor);
                clickInfo.event.setProp('textColor', textColor);
                clickInfo.event.setExtendedProp('payable', newPayable);
            }
        }
    }, [isEditing, selectedTrainer, isEditingPayable, trainers, getTrainerColor, classes]);

    // Memoized event content renderer
    const renderEventContent = useCallback((eventInfo) => {
        const { trainerName, payable } = eventInfo.event.extendedProps;
        const startTime = new Date(eventInfo.event.start).toLocaleTimeString('et-EE', {
            hour: '2-digit',
            minute: '2-digit'
        });

        return (
            <Box sx={{ p: 0.5, fontSize: '0.75rem', lineHeight: 1.2 }}>
                <Box sx={{ fontWeight: 'bold' }}>{startTime}</Box>
                <Box sx={{ fontWeight: 'medium' }}>{eventInfo.event.title}</Box>
                {trainerName && (
                    <Box sx={{
                        fontStyle: 'italic',
                        opacity: 0.9,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5
                    }}>
                        <PersonIcon sx={{ fontSize: '0.75rem' }} />
                        {trainerName}
                    </Box>
                )}
                {(isEditingPayable || payable === false) && (
                    <Box sx={{
                        fontSize: '0.65rem',
                        fontWeight: 'bold',
                        opacity: 0.8
                    }}>
                        {payable ? '💰' : '🚫'} {payable ? 'PAYABLE' : 'NOT PAYABLE'}
                    </Box>
                )}
            </Box>
        );
    }, [isEditingPayable]);

    // Handler functions with useCallback for performance
    const handleEdit = useCallback(() => {
        setIsEditing(true);
        setIsEditingPayable(false);
        setPendingChanges(new Map());
        setPendingPayableChanges(new Map());
        setSelectedTrainer('');
    }, []);

    const handleEditPayable = useCallback(() => {
        setIsEditingPayable(true);
        setIsEditing(false);
        setPendingChanges(new Map());
        setPendingPayableChanges(new Map());
        setSelectedTrainer('');
    }, []);

    const handleCancel = useCallback(() => {
        setIsEditing(false);
        setIsEditingPayable(false);
        setSelectedTrainer('');
        setPendingChanges(new Map());
        setPendingPayableChanges(new Map());
        loadClassSchedules();
    }, [loadClassSchedules]);

    const handleToggleFilter = useCallback(() => {
        setIsFiltering(prev => !prev);
        if (isFiltering) {
            setFilterTrainer('');
        }
    }, [isFiltering]);

    const handleFilterTrainerChange = useCallback((trainerId) => {
        setFilterTrainer(trainerId);
    }, []);

    // Optimized save function
    const handleSave = useCallback(async () => {
        const hasTrainerChanges = pendingChanges.size > 0;
        const hasPayableChanges = pendingPayableChanges.size > 0;

        if (!hasTrainerChanges && !hasPayableChanges) {
            setIsEditing(false);
            setIsEditingPayable(false);
            return;
        }

        try {
            const requests = [];
            const token = localStorage.getItem('token');

            if (hasTrainerChanges) {
                const trainerUpdates = Array.from(pendingChanges.entries()).map(([classId, trainerName]) => {
                    const trainer = trainers.find(t => t.fullName === trainerName);
                    return {
                        classId: classId,
                        trainerId: trainer ? trainer.trainerId : null,
                        trainerName: trainerName
                    };
                });

                requests.push(fetch(`${API_URL}/trainer-schedule/bulk-update`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        affiliateId: affiliate.id,
                        updates: trainerUpdates
                    })
                }));
            }

            if (hasPayableChanges) {
                const payableUpdates = Array.from(pendingPayableChanges.entries()).map(([classId, payable]) => ({
                    classId: classId,
                    payable: payable
                }));

                requests.push(fetch(`${API_URL}/trainer-schedule/bulk-update-payable`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'application/json'
                    },
                    body: JSON.stringify({
                        affiliateId: affiliate.id,
                        updates: payableUpdates
                    })
                }));
            }

            const responses = await Promise.all(requests);

            for (const response of responses) {
                if (!response.ok) {
                    throw new Error('Failed to update schedules');
                }
            }

            setSuccessDialogOpen(true);
            setIsEditing(false);
            setIsEditingPayable(false);
            setPendingChanges(new Map());
            setPendingPayableChanges(new Map());
            setSelectedTrainer('');
            await loadClassSchedules();
        } catch (err) {
            setError(err.message);
        }
    }, [pendingChanges, pendingPayableChanges, trainers, affiliate.id, loadClassSchedules]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h5" gutterBottom fontWeight="bold">
                Trainer Schedule
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Trainer Color Legend - Only render when needed */}
            {trainers.length > 0 && (
                <TrainerColorLegend
                    trainers={trainers}
                    expanded={legendExpanded}
                    onToggle={() => setLegendExpanded(!legendExpanded)}
                />
            )}

            {/* Control Panel */}
            <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                {!isEditing && !isEditingPayable ? (
                    <>
                        <Button
                            variant={isFiltering ? "contained" : "outlined"}
                            color={isFiltering ? "primary" : "inherit"}
                            startIcon={isFiltering ? <FilterListIcon /> : <FilterListOffIcon />}
                            onClick={handleToggleFilter}
                            disabled={classes.length === 0}
                        >
                            {isFiltering ? 'Hide Filter' : 'Filter by Trainer'}
                        </Button>

                        {isFiltering && (
                            <FormControl sx={{ minWidth: 200 }}>
                                <InputLabel>Filter by Trainer</InputLabel>
                                <Select
                                    value={filterTrainer}
                                    label="Filter by Trainer"
                                    onChange={(e) => handleFilterTrainerChange(e.target.value)}
                                >
                                    <MenuItem value="">
                                        <em>Show All Trainers</em>
                                    </MenuItem>
                                    {trainers.map((trainer) => (
                                        <MenuItem key={trainer.trainerId} value={trainer.trainerId.toString()}>
                                            {trainer.fullName}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}
                                <Button
                                    variant="contained"
                                    startIcon={<EditIcon />}
                                    onClick={handleEdit}
                                    disabled={classes.length === 0}
                                >
                                    Edit Schedule
                                </Button>

                        {isAffiliate && (
                            <>


                                <Button
                                    variant="contained"
                                    color="secondary"
                                    startIcon={<EditIcon />}
                                    onClick={handleEditPayable}
                                    disabled={classes.length === 0}
                                >
                                    Edit Payable Status
                                </Button>
                            </>
                        )}

                        <Button
                            variant="outlined"
                            startIcon={<BarChartIcon />}
                            onClick={() => setStatsModalOpen(true)}
                            disabled={classes.length === 0}
                            color="info"
                        >
                            View Statistics
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<BeachAccessIcon />}
                            onClick={() => setVacationModalOpen(true)}
                            color="secondary"
                        >
                            Vacations {currentYear}
                        </Button>
                    </>
                ) : isEditing ? (
                    <>
                        <FormControl sx={{ minWidth: 200 }}>
                            <InputLabel>Select Trainer</InputLabel>
                            <Select
                                value={selectedTrainer}
                                label="Select Trainer"
                                onChange={(e) => setSelectedTrainer(e.target.value)}
                            >
                                <MenuItem value="">
                                    <em>No Trainer</em>
                                </MenuItem>
                                {trainers.map((trainer) => (
                                    <MenuItem key={trainer.trainerId} value={trainer.trainerId}>
                                        {trainer.fullName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={pendingChanges.size === 0 && pendingPayableChanges.size === 0}
                        >
                            Save Changes
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>

                        {pendingChanges.size > 0 && (
                            <Chip
                                label={`${pendingChanges.size} trainer change${pendingChanges.size > 1 ? 's' : ''} pending`}
                                color="warning"
                                size="small"
                            />
                        )}
                    </>
                ) : (
                    <>
                        <Button
                            variant="contained"
                            color="success"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={pendingPayableChanges.size === 0}
                        >
                            Save Changes
                        </Button>

                        <Button
                            variant="outlined"
                            startIcon={<CloseIcon />}
                            onClick={handleCancel}
                        >
                            Cancel
                        </Button>

                        {pendingPayableChanges.size > 0 && (
                            <Chip
                                label={`${pendingPayableChanges.size} payable change${pendingPayableChanges.size > 1 ? 's' : ''} pending`}
                                color="warning"
                                size="small"
                            />
                        )}
                    </>
                )}
            </Box>

            {/* Filter Info */}
            {isFiltering && filterTrainer && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Showing classes for: <strong>{trainers.find(t => t.trainerId.toString() === filterTrainer)?.fullName || 'Unknown Trainer'}</strong>
                    {' '}
                    <Button
                        size="small"
                        onClick={() => handleFilterTrainerChange('')}
                        sx={{ ml: 1 }}
                    >
                        Clear Filter
                    </Button>
                </Alert>
            )}

            {/* Edit Instructions */}
            {isEditing && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Select a trainer from the dropdown, then click on class sessions to assign them.
                </Alert>
            )}

            {isEditingPayable && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    Click on class sessions to toggle their payable status. Colored classes are payable, gray classes are not payable.
                </Alert>
            )}

            {/* Calendar and Notes Grid */}
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    {isMobile ? (
                        /* Mobile Week-by-Week View */
                        <TrainerScheduleMobile
                            affiliate={affiliate}
                            trainers={trainers}
                            classes={filteredClasses}
                            visibleWeeks={visibleWeeks}
                            getTrainerColor={getTrainerColor}
                            onClassClick={handleEventClick}
                            isEditing={isEditing}
                            isEditingPayable={isEditingPayable}
                            pendingChanges={pendingChanges}
                            pendingPayableChanges={pendingPayableChanges}
                        />
                    ) : (
                        /* Desktop Calendar View */
                        <CalendarPaper>
                            {classes.length === 0 ? (
                                <Box sx={{ textAlign: 'center', p: 4 }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No class schedules found for this affiliate.
                                    </Typography>
                                </Box>
                            ) : (
                                <Box sx={{ 
                                    display: 'flex', 
                                    gap: 0
                                }}>
                                    {/* Calendar Section */}
                                    <Box sx={{
                                        flex: '1 1 auto',
                                        minWidth: 0,
                                        '.fc': { fontFamily: 'inherit' },
                                        '.fc-toolbar-title': { fontSize: '1.2em' },
                                        '.fc-event': {
                                            cursor: (isEditing || isEditingPayable) ? 'pointer' : 'default',
                                            border: '1px solid',
                                            borderRadius: '4px'
                                        },
                                        '.fc-event:hover': (isEditing || isEditingPayable) ? {
                                            opacity: 0.8,
                                            transform: 'scale(1.02)',
                                            transition: 'all 0.2s ease'
                                        } : {}
                                    }}>
                                        <FullCalendar
                                            ref={calendarRef}
                                            plugins={[dayGridPlugin]}
                                            initialView="dayGridMonth"
                                            events={calendarEvents}
                                            eventClick={handleEventClick}
                                            eventContent={renderEventContent}
                                            datesSet={handleDatesSet}
                                            height="auto"
                                            firstDay={1}
                                            headerToolbar={{
                                                left: 'prev',
                                                center: 'title',
                                                right: 'today next'
                                            }}
                                            eventDisplay="block"
                                            dayMaxEvents={false}
                                        />
                                    </Box>

                                    {/* Notes Column - "8th Day" next to calendar */}
                                    <Box sx={{ 
                                        flex: '0 0 200px',
                                        minWidth: 200,
                                        maxWidth: 200,
                                        overflow: 'hidden'
                                    }}>
                                        <TrainerWeeklyNotes
                                            affiliate={affiliate}
                                            trainers={trainers}
                                            currentWeekStart={currentWeekStart}
                                            visibleWeeks={visibleWeeks}
                                            isMobile={false}
                                        />
                                    </Box>
                                </Box>
                            )}
                        </CalendarPaper>
                    )}
                </Grid>
            </Grid>

            {/* Success Dialog */}
            <Dialog
                open={successDialogOpen}
                onClose={() => setSuccessDialogOpen(false)}
            >
                <DialogTitle>Success</DialogTitle>
                <DialogContent>
                    <Typography>Trainer schedule updated successfully!</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setSuccessDialogOpen(false)} color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Trainer Statistics Modal */}
            <TrainerStatsModal
                open={statsModalOpen}
                onClose={() => setStatsModalOpen(false)}
                affiliate={affiliate}
                trainers={trainers}
            />

            {/* Trainer Vacation Modal */}
            <TrainerVacationModal
                open={vacationModalOpen}
                onClose={() => setVacationModalOpen(false)}
                affiliate={affiliate}
                trainers={trainers}
                currentYear={currentYear}
            />
        </Box>
    );
}